import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

const EnhancedCarousel = ({ items }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % items.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
  };

  useEffect(() => {
    const timer = setInterval(nextSlide, 10000);
    return () => clearInterval(timer);
  }, []);

  const openModal = () => {
    setSelectedItem(items[currentIndex]);
  };

  const closeModal = () => {
    setSelectedItem(null);
  };

  // Close modal on Escape key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      <div className="relative w-full h-[350px] bg-gray-100 dark:bg-gray-900 rounded-3xl overflow-hidden shadow-2xl">
        {items.map((item, index) => (
          <div 
            key={index} 
            className={`
              absolute inset-0 transition-opacity duration-700 
              ${index === currentIndex ? 'opacity-100' : 'opacity-0'}
            `}
          >
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${item.backgroundImage})` }}
            >
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <div className="text-center text-white max-w-2xl px-8">
                  <h2 className="text-4xl font-bold mb-4">{item.title}</h2>
                  <p className="text-xl mb-6">{item.description}</p>
                  <button 
                    onClick={openModal}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full transition-colors"
                  >
                    Learn More
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
        <button 
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 p-3 rounded-full"
        >
          <ChevronLeft className="text-white" />
        </button>
        <button 
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 p-3 rounded-full"
        >
          <ChevronRight className="text-white" />
        </button>
      </div>

      {/* Modal */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full p-8 relative max-h-[90vh] overflow-y-auto">
            <button 
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="flex flex-col md:flex-row gap-8">
              <div className="md:w-1/2">
                <img 
                  src={selectedItem.backgroundImage} 
                  alt={selectedItem.title}
                  className="rounded-lg w-full h-64 object-cover shadow-lg"
                />
                <div className="mt-4 flex justify-center space-x-4">
                  <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full">
                    Request Demo
                  </button>
                  <button className="border border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 px-6 py-2 rounded-full">
                    Documentation
                  </button>
                </div>
              </div>
              
              <div className="md:w-1/2">
                <h3 className="text-2xl font-bold mb-4 dark:text-white">{selectedItem.title}</h3>
                <p className="mb-6 text-gray-700 dark:text-gray-300">{selectedItem.description}</p>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-lg dark:text-white">Key Features</h4>
                    <ul className="mt-2 space-y-2">
                      {selectedItem.features?.map((feature, i) => (
                        <li key={i} className="flex items-start">
                          <span className="text-green-500 mr-2">✓</span>
                          <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                        </li>
                      )) || (
                        <li className="text-gray-500 dark:text-gray-400">No additional features specified</li>
                      )}
                    </ul>
                  </div>
                  
                  {selectedItem.technologies && (
                    <div>
                      <h4 className="font-semibold text-lg dark:text-white">Technologies</h4>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {selectedItem.technologies.map((tech, i) => (
                          <span key={i} className="bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full text-sm">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EnhancedCarousel;